// Three.js renderer/scene/camera + the shared chunk material (client).
// Directional shade × AO is baked into the vertex color; sky/block light arrive
// as per-vertex attributes (aSky/aBlock) and are combined in-shader with a
// shared `uDaylight` uniform (LIT-5) — so dusk is a uniform tweak, not a remesh.
import * as THREE from 'three';
import { LIGHT } from '../../sim/config';
import { FOV_DEFAULT } from '../fov';
import { makeAtlasTexture } from './textures/atlas';
import { applySky, type Sky } from './sky';

export interface RenderContext {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  material: THREE.MeshBasicMaterial;
  /** Translucent pass (water) — transparent, no depth write. */
  waterMaterial: THREE.MeshBasicMaterial;
  atlasCanvas: HTMLCanvasElement;
  sky: Sky;
  /** Shared sky-light scale (0..1); set each frame from daylightFactor. */
  daylight: { value: number };
}

/** The GLSL that turns aSky/aBlock + uDaylight into a brightness multiply.
 *  brightness = CURVE_BASE^(MAX − level), floored at MIN_BRIGHTNESS. */
function installLightShader(material: THREE.MeshBasicMaterial, daylight: { value: number }): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uDaylight = daylight;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aSky;\nattribute float aBlock;\nvarying float vSky;\nvarying float vBlock;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSky = aSky;\nvBlock = aBlock;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uDaylight;\nvarying float vSky;\nvarying float vBlock;')
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        float bbLevel = max(vBlock, vSky * uDaylight) * ${LIGHT.MAX.toFixed(1)};
        float bbBright = max(pow(${LIGHT.CURVE_BASE.toFixed(3)}, ${LIGHT.MAX.toFixed(1)} - bbLevel), ${LIGHT.MIN_BRIGHTNESS.toFixed(3)});
        diffuseColor.rgb *= bbBright;`,
      );
  };
  // Distinct cache key so this program isn't shared with an un-patched basic mat.
  material.customProgramCacheKey = () => 'bb-chunk-light';
}

export function createRenderContext(mount: HTMLElement): RenderContext {
  const renderer = new THREE.WebGLRenderer({ antialias: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const sky = applySky(scene);

  const camera = new THREE.PerspectiveCamera(FOV_DEFAULT, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.rotation.order = 'YXZ';

  const daylight = { value: LIGHT.DAY };
  const { texture, canvas } = makeAtlasTexture();
  const material = new THREE.MeshBasicMaterial({ map: texture, vertexColors: true, alphaTest: 0.5 });
  const waterMaterial = new THREE.MeshBasicMaterial({
    map: texture,
    vertexColors: true,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
    // Default FrontSide culls every water face from the inside — a submerged
    // camera would see no water at all (the surface overhead, pool walls) since
    // all of it is backfacing from underwater. Water needs both sides visible.
    side: THREE.DoubleSide,
  });
  installLightShader(material, daylight);
  installLightShader(waterMaterial, daylight);

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  return { renderer, scene, camera, material, waterMaterial, atlasCanvas: canvas, sky, daylight };
}
